export type PresentationCadenceState = 'active' | 'ambient' | 'quiescent';

export interface PresentationCadenceOptions {
  activeFps: number;
  ambientFps: number;
  quiescentFps: number;
  activeHoldMs: number;
  ambientHoldMs: number;
}

export interface PresentationCadenceDecision {
  state: PresentationCadenceState;
  fps: number;
  changed: boolean;
}

/**
 * Pure active, ambient, and quiescent presentation cadence policy.
 *
 * Input wakes active presentation immediately.
 * Replica and asset invalidations wake ambient presentation.
 * Network processing and command delivery remain outside this policy.
 */
export class PresentationCadence {
  private state?: PresentationCadenceState;
  private activeUntil = Number.NEGATIVE_INFINITY;
  private ambientUntil = Number.NEGATIVE_INFINITY;

  constructor(private readonly options: PresentationCadenceOptions) {}

  wake(nowMs: number): PresentationCadenceDecision {
    this.activeUntil = Math.max(this.activeUntil, nowMs + this.options.activeHoldMs);
    this.ambientUntil = Math.max(this.ambientUntil, nowMs + this.options.ambientHoldMs);
    return this.choose('active');
  }

  invalidate(nowMs: number): PresentationCadenceDecision {
    this.ambientUntil = Math.max(this.ambientUntil, nowMs + this.options.ambientHoldMs);
    return this.choose(nowMs < this.activeUntil ? 'active' : 'ambient');
  }

  evaluate(nowMs: number, active: boolean): PresentationCadenceDecision {
    if (this.state === undefined) return this.wake(nowMs);
    if (active) return this.wake(nowMs);
    if (nowMs < this.activeUntil) return this.choose('active');
    if (nowMs < this.ambientUntil) return this.choose('ambient');
    return this.choose('quiescent');
  }

  private choose(state: PresentationCadenceState): PresentationCadenceDecision {
    const changed = this.state !== state;
    this.state = state;
    const fps =
      state === 'active'
        ? this.options.activeFps
        : state === 'ambient'
          ? this.options.ambientFps
          : this.options.quiescentFps;
    return { state, fps, changed };
  }
}
